/**
 * Thai Address Database and Utilities
 * Provides autocomplete, validation, and parsing for Thai addresses
 *
 * Data sources:
 * - Default: try CDN first (thai-data npm), fallback to local file if CDN fails.
 * - Set REACT_APP_THAI_ADDRESS_LOCAL_ONLY=true to skip CDN and use only local file.
 */

// Local data file (public/data/thai-addresses.json)
const THAI_ADDRESS_DATA_PATH = '/data/thai-addresses.json';

// CDN: thai-data (npm) - ตำบล อำเภอ จังหวัด by รหัสไปรษณีย์; zipCode, subDistrictList, districtList, provinceList
// https://www.npmjs.com/package/thai-data | https://github.com/niawjunior/thai-data
const THAI_ADDRESS_CDN_URL = 'https://cdn.jsdelivr.net/npm/thai-data@3.0.2/data.json';

let thaiAddressDatabase = null;
let isLoading = false;
let loadPromise = null;
let loadError = null;
let loadSource = null; // 'local' | 'cdn'

/**
 * Normalize address data to consistent format
 * Handles different data structures from various sources
 * 
 * Common formats:
 * Format 1 (jquery.Thailand.js): { district: "ตำบล", amphoe: "อำเภอ", province, zipcode }
 * Format 2 (standard): { subdistrict: "ตำบล", district: "อำเภอ", province, postcode }
 * Format 3 (Thai names): { tambon: "ตำบล", amphoe: "อำเภอ", changwat, zipcode }
 */
const normalizeAddressData = (data) => {
  if (!Array.isArray(data)) {
    console.error('normalizeAddressData: data is not an array', typeof data);
    return [];
  }
  
  const normalized = data.map((item, index) => {
    // Detect format and normalize
    // Format 1: jquery.Thailand.js uses "district" for tambon and "amphoe" for amphoe
    const hasAmphoe = item.amphoe || item.amphur;
    
    let subdistrict, district;
    
    if (hasAmphoe) {
      // Format 1 or 3: district field = tambon, amphoe field = amphoe
      subdistrict = item.district || item.tambon || item.tumbon || item.sub_district || '';
      district = item.amphoe || item.amphur || '';
    } else {
      // Format 2: standard naming
      subdistrict = item.subdistrict || item.tambon || item.tumbon || item.sub_district || '';
      district = item.district || '';
    }
    
    return {
      subdistrict: subdistrict.trim(),
      district: district.trim(),
      province: (item.province || item.changwat || item.province_name || '').trim(),
      postcode: String(item.postcode || item.zipcode || item.post_code || item.zip_code || '').trim()
    };
  });
  
  // Filter out items with missing required fields and log first few invalid ones
  const valid = normalized.filter(item => {
    const isValid = item.subdistrict && item.district && item.province;
    if (!isValid && normalized.indexOf(item) < 3) {
      console.warn('Invalid address record (first 3):', item);
    }
    return isValid;
  });
  
  if (valid.length === 0 && normalized.length > 0) {
    console.error('All records were filtered out. Sample original:', data[0]);
    console.error('Sample normalized (before filter):', normalized[0]);
  }
  
  return valid;
};

/**
 * Normalize thailand-geography-json format to our flat format.
 * Keys: subdistrictNameTh, districtNameTh, provinceNameTh, postalCode
 */
const normalizeGeographyFormat = (data) => {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => {
      const subdistrict = (item.subdistrictNameTh || item.subdistrict || '').trim();
      const district = (item.districtNameTh || item.district || '').trim();
      const province = (item.provinceNameTh || item.province || '').trim();
      const postcode = String(item.postalCode ?? item.postcode ?? item.zipcode ?? '').trim();
      return { subdistrict, district, province, postcode };
    })
    .filter((item) => item.subdistrict && item.district && item.province);
};

/**
 * Normalize thai-data (npm) format to our flat format.
 * Structure: array of { zipCode, subDistrictList: [{ subDistrictName, districtId }], districtList: [{ districtName, districtId }], provinceList: [{ provinceName }] }
 * @see https://cdn.jsdelivr.net/npm/thai-data
 */
const normalizeThaiDataFormat = (data) => {
  if (!Array.isArray(data)) return [];
  const flat = [];
  for (const item of data) {
    const postcode = String(item.zipCode ?? '').trim();
    const province = (item.provinceList?.[0]?.provinceName ?? '').trim();
    if (!postcode || !province) continue;
    const districtList = item.districtList ?? [];
    const subDistrictList = item.subDistrictList ?? [];
    for (const sub of subDistrictList) {
      const districtObj = districtList.find((d) => d.districtId === sub.districtId);
      const district = (districtObj?.districtName ?? '').trim();
      const subdistrict = (sub.subDistrictName ?? sub.subDistrict ?? '').trim();
      if (subdistrict && district) flat.push({ subdistrict, district, province, postcode });
    }
  }
  return flat;
};

const localOnly = () => process.env.REACT_APP_THAI_ADDRESS_LOCAL_ONLY === 'true';

const loadFromUrl = (url, options = {}) => {
  const { useGeographyFormat = false, useThaiDataFormat = false } = options;
  return fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      return response.json();
    })
    .then((data) => {
      if (!data || !Array.isArray(data)) throw new Error(`Invalid format: expected array, got ${typeof data}`);
      let normalized;
      if (useThaiDataFormat) normalized = normalizeThaiDataFormat(data);
      else if (useGeographyFormat) normalized = normalizeGeographyFormat(data);
      else normalized = normalizeAddressData(data);
      if (normalized.length === 0) throw new Error('No valid records after normalization');
      return normalized;
    });
};

/**
 * Load the Thai address database: try CDN first, fallback to local file if CDN fails.
 * Set REACT_APP_THAI_ADDRESS_LOCAL_ONLY=true to use only the local file (no CDN).
 * @returns {Promise<Array|null>} The address database or null if failed
 */
export const loadThaiAddressDatabase = async () => {
  if (thaiAddressDatabase) return thaiAddressDatabase;
  if (loadPromise) return loadPromise;

  isLoading = true;
  loadError = null;

  const tryLoad = (fromCdn) => {
    const url = fromCdn ? THAI_ADDRESS_CDN_URL : THAI_ADDRESS_DATA_PATH;
    const useThaiDataFormat = fromCdn;
    return loadFromUrl(url, { useThaiDataFormat })
      .then((normalizedData) => {
        loadSource = fromCdn ? 'cdn' : 'local';
        console.log(`✅ Thai address loaded from ${loadSource}: ${normalizedData.length} records`);
        thaiAddressDatabase = normalizedData;
        loadError = null;
        return normalizedData;
      })
      .catch((error) => {
        console.warn(`Thai address load failed (${fromCdn ? 'cdn' : 'local'}):`, error.message);
        throw error;
      });
  };

  loadPromise = (async () => {
    try {
      if (localOnly()) {
        return await tryLoad(false);
      }
      try {
        return await tryLoad(true);
      } catch (cdnError) {
        console.warn('Thai address: CDN failed, falling back to local file');
        return await tryLoad(false);
      }
    } catch (err) {
      loadError = err.message;
      console.error('❌ Failed to load Thai address database:', err.message);
      thaiAddressDatabase = null;
      return null;
    } finally {
      isLoading = false;
    }
  })();

  return loadPromise;
};

/** Which source was used: 'local' | 'cdn' (null if not loaded yet) */
export const getThaiAddressLoadSource = () => loadSource;

/**
 * Check if the database is available
 * @returns {boolean}
 */
export const isDatabaseAvailable = () => {
  return thaiAddressDatabase !== null && thaiAddressDatabase.length > 0;
};

/**
 * Get the load error message if any
 * @returns {string|null}
 */
export const getLoadError = () => loadError;

/**
 * Search the database by any field
 * @param {string} query - Search query
 * @param {string} field - Field to search (subdistrict, district, province, postcode)
 * @param {number} limit - Maximum results to return
 * @returns {Array} Matching address records (empty if database not available)
 */
export const searchByField = (query, field, limit = 20) => {
  if (!isDatabaseAvailable() || !query || query.length < 1) {
    return [];
  }

  const normalizedQuery = query.toString().toLowerCase().trim();
  
  const results = thaiAddressDatabase.filter(item => {
    const fieldValue = item[field];
    if (!fieldValue) return false;
    return fieldValue.toString().toLowerCase().startsWith(normalizedQuery);
  });

  return results.slice(0, limit);
};

/**
 * Search with multiple criteria for more accurate filtering
 * @param {Object} criteria - { subdistrict, district, province, postcode }
 * @param {number} limit - Maximum results
 * @returns {Array} Matching records (empty if database not available)
 */
export const searchByCriteria = (criteria, limit = 20) => {
  if (!isDatabaseAvailable()) {
    return [];
  }

  let results = [...thaiAddressDatabase];

  // Filter by each provided criterion
  if (criteria.province && criteria.province.trim()) {
    const q = criteria.province.toLowerCase().trim();
    results = results.filter(item => 
      item.province && item.province.toLowerCase().includes(q)
    );
  }

  if (criteria.district && criteria.district.trim()) {
    const q = criteria.district.toLowerCase().trim();
    results = results.filter(item => 
      item.district && item.district.toLowerCase().includes(q)
    );
  }

  if (criteria.subdistrict && criteria.subdistrict.trim()) {
    const q = criteria.subdistrict.toLowerCase().trim();
    results = results.filter(item => 
      item.subdistrict && item.subdistrict.toLowerCase().includes(q)
    );
  }

  if (criteria.postcode && criteria.postcode.trim()) {
    const q = criteria.postcode.trim();
    results = results.filter(item => 
      item.postcode && item.postcode.toString().startsWith(q)
    );
  }

  return results.slice(0, limit);
};

/**
 * Validate if the address combination is valid
 * @param {Object} address - { subdistrict, district, province, postcode }
 * @returns {Object} { isValid, errors, suggestions, databaseUnavailable }
 */
export const validateAddress = (address) => {
  // If database is not available, skip validation and indicate it
  if (!isDatabaseAvailable()) {
    return { 
      isValid: true, // Don't block submission
      errors: [], 
      suggestions: [],
      databaseUnavailable: true 
    };
  }

  const errors = [];
  const { subdistrict, district, province, postcode } = address;

  // Check if postcode exists
  if (postcode) {
    const postcodeMatches = thaiAddressDatabase.filter(
      item => item.postcode && item.postcode.toString() === postcode.toString()
    );
    
    if (postcodeMatches.length === 0) {
      errors.push(`รหัสไปรษณีย์ ${postcode} ไม่ถูกต้อง`);
    } else {
      // Check if postcode matches the other fields
      if (province && !postcodeMatches.some(item => item.province === province)) {
        errors.push(`รหัสไปรษณีย์ ${postcode} ไม่ตรงกับจังหวัด ${province}`);
      }
      if (district && !postcodeMatches.some(item => item.district === district)) {
        errors.push(`รหัสไปรษณีย์ ${postcode} ไม่ตรงกับอำเภอ/เขต ${district}`);
      }
      if (subdistrict && !postcodeMatches.some(item => item.subdistrict === subdistrict)) {
        errors.push(`รหัสไปรษณีย์ ${postcode} ไม่ตรงกับตำบล/แขวง ${subdistrict}`);
      }
    }
  }

  // Check if province-district-subdistrict combination exists; when postcode is set, require full quadruple match
  if (province && district && subdistrict) {
    const matches = thaiAddressDatabase.filter(
      item => item.province === province &&
              item.district === district &&
              item.subdistrict === subdistrict
    );

    if (matches.length === 0) {
      errors.push(`ไม่พบ ${subdistrict}, ${district}, ${province} ในฐานข้อมูล`);
    } else if (postcode) {
      // All four set: require (subdistrict, district, province, postcode) to exist
      const fullMatch = matches.some(item => item.postcode?.toString() === postcode.toString());
      if (!fullMatch) {
        errors.push(`รหัสไปรษณีย์ ${postcode} ไม่ตรงกับ ${subdistrict}, ${district}, ${province}`);
      }
    } else {
      // No postcode but multiple postcodes exist for this triple: user must choose
      const postcodes = [...new Set(matches.map(item => item.postcode?.toString()).filter(Boolean))];
      if (postcodes.length > 1) {
        errors.push(`กรุณาเลือกรหัสไปรษณีย์ (มีได้หลายรหัสสำหรับตำบลนี้)`);
      }
    }
  }

  // Find suggestions if there are errors
  let suggestions = [];
  if (errors.length > 0 && (subdistrict || district || province || postcode)) {
    suggestions = searchByCriteria({
      subdistrict: subdistrict || '',
      district: district || '',
      province: province || '',
      postcode: postcode || ''
    }, 5);
  }

  // Log pass/fail for address filter (for debugging e.g. ธนู อุทัย พระนครศรีอยุธยา 13210/13000)
  const logLabel = '[ThaiAddress] validate';
  if (province && district && subdistrict) {
    const matches = thaiAddressDatabase.filter(
      item => item.province === province && item.district === district && item.subdistrict === subdistrict
    );
    const postcodesInDb = [...new Set(matches.map(item => item.postcode?.toString()).filter(Boolean))].sort();
    if (errors.length === 0) {
      console.log(logLabel, 'PASS', { subdistrict, district, province, postcode: postcode || null, postcodesInDb });
    } else {
      console.log(logLabel, 'FAIL', { subdistrict, district, province, postcode: postcode || null, postcodesInDb, errors });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    suggestions,
    databaseUnavailable: false
  };
};

/**
 * Get correct postcode for a given address (first match only; use getPostcodesForAddress when multiple postcodes exist).
 * @param {string} subdistrict
 * @param {string} district 
 * @param {string} province
 * @returns {string|null} The correct postcode or null
 */
export const getPostcodeForAddress = (subdistrict, district, province) => {
  const postcodes = getPostcodesForAddress(subdistrict, district, province);
  return postcodes.length > 0 ? postcodes[0] : null;
};

/**
 * Get all postcodes for a given subdistrict/district/province (same triple can have multiple postcodes in Thailand).
 * @param {string} subdistrict
 * @param {string} district 
 * @param {string} province
 * @returns {string[]} Array of unique postcodes (empty if none)
 */
export const getPostcodesForAddress = (subdistrict, district, province) => {
  if (!isDatabaseAvailable()) return [];

  const matches = thaiAddressDatabase.filter(
    item => item.subdistrict === subdistrict &&
            item.district === district &&
            item.province === province
  );

  const postcodes = [...new Set(matches.map(item => item.postcode?.toString()).filter(Boolean))].sort();
  if (postcodes.length > 1) {
    console.log('[ThaiAddress] getPostcodesForAddress multiple postcodes for same triple', { subdistrict, district, province, postcodes });
  }
  return postcodes;
};

/**
 * Normalize Thai text - remove zero-width characters and normalize Unicode
 * @param {string} text 
 * @returns {string} Normalized text
 */
const normalizeThaiText = (text) => {
  if (!text) return '';
  return text
    // Remove zero-width characters (common in copy-paste)
    .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '')
    // Normalize Unicode (NFC form)
    .normalize('NFC')
    .trim();
};

/**
 * Extract basic info from pasted text (phone, postcode, name)
 * @param {string} fullText - The pasted text
 * @returns {Object} { phone, postcode, name, rawText, cleanText, phonePrefix }
 */
export const extractBasicInfo = (fullText) => {
  if (!fullText || typeof fullText !== 'string') {
    return { phone: '', postcode: '', name: '', rawText: '', cleanText: '', phonePrefix: '' };
  }

  // Strip double quotes from beginning and end if they wrap the entire text
  let processedText = fullText.trim();
  if ((processedText.startsWith('"') && processedText.endsWith('"')) || 
      (processedText.startsWith('"') && processedText.endsWith('"'))) {
    processedText = processedText.slice(1, -1).trim();
  }

  // Normalize the text first
  const cleanText = normalizeThaiText(processedText);
  const result = { phone: '', postcode: '', name: '', rawText: fullText, cleanText, phonePrefix: '' };

  // Extract phone number with various formats and prefixes
  // Match: Tel : 0618293649, Tel: 0618293649, โทร 0618293649, 0618293649, etc.
  const phonePatterns = [
    /(?:Tel|โทร|เบอร์|เบอร์โทร|phone|mobile|มือถือ)[\s:：]*([0-9]{9,10})/i,
    /([0][0-9]{1,2}[-\s]?[0-9]{3,4}[-\s]?[0-9]{4})/,
    /([0][0-9]{9})/,
  ];

  for (const pattern of phonePatterns) {
    const phoneMatch = cleanText.match(pattern);
    if (phoneMatch) {
      result.phone = phoneMatch[1].replace(/[-\s]/g, '');
      // Extract the prefix if it exists
      const fullMatch = phoneMatch[0];
      if (fullMatch !== phoneMatch[1]) {
        result.phonePrefix = fullMatch.replace(phoneMatch[1], '').trim();
      }
      break;
    }
  }

  // Extract postcode (5 digits)
  const postcodeMatch = cleanText.match(/\b(\d{5})\b/);
  if (postcodeMatch) {
    result.postcode = postcodeMatch[1];
  }

  // Extract name - try multiple strategies
  // Strategy 1: First line(s) that look like a name (Thai/English letters, parentheses, 2-50 chars)
  const lines = cleanText.split(/\n/).map(l => l.trim()).filter(l => l);
  if (lines.length > 0) {
    let firstLine = lines[0];
    // Strip quotes from the line if present
    firstLine = firstLine.replace(/^["""]+|["""]+$/g, '').trim();
    
    // Check if first line looks like a name (not containing numbers, not too long, mostly letters)
    // Allow "คุณ" prefix as it's a common Thai honorific
    // Allow parentheses, brackets, and common punctuation in names
    if (firstLine && 
        !firstLine.match(/\d{5}/) && // Not a postcode
        !firstLine.match(/[0-9]{9,10}/) && // Not a phone
        !firstLine.match(/(ซอย|ถนน|แขวง|เขต|จังหวัด|อำเภอ|ตำบล|ม\.|หมู่|Tel|โทร|เบอร์|บริษัท)/i) && // Not address/phone keywords (but allow "คุณ")
        /^[ก-๙a-zA-Z\s\.\(\)\[\]\/\-"]+$/.test(firstLine) && // Letters, spaces, dots, parentheses, brackets, slashes, hyphens, quotes
        firstLine.length >= 2 && 
        firstLine.length <= 80) { // Increased length limit for names with additional info
      // Remove any remaining quotes from the name
      result.name = firstLine.replace(/["""]+/g, '').trim();
    }
  }

  // Strategy 2: First few words before address keywords or numbers
  if (!result.name) {
    // Try to find name at the start - words before first number or address keyword
    // Allow parentheses and other common characters, including quotes
    const nameMatch = cleanText.match(/^([ก-๙a-zA-Z\s\.\(\)\[\]\/\-"]{2,80}?)(?=\s*(?:\d|ซอย|ถนน|แขวง|เขต|จังหวัด|อำเภอ|ตำบล|ม\.|หมู่|Tel|โทร|เบอร์|บริษัท))/i);
    if (nameMatch) {
      // Remove quotes from the matched name
      const potentialName = nameMatch[1].replace(/["""]+/g, '').trim();
      if (potentialName && 
          potentialName.length >= 2 && 
          potentialName.length <= 80 &&
          !potentialName.match(/\d{5}/) && // Not a postcode
          !potentialName.match(/[0-9]{9,10}/)) { // Not a phone
        result.name = potentialName;
      }
    }
  }

  // Strategy 3: If no name found and we have phone, try text before phone
  if (!result.name && result.phone) {
    const phoneIndex = cleanText.indexOf(result.phone.substring(0, 3));
    if (phoneIndex > 0) {
      const beforePhone = cleanText.substring(0, phoneIndex).trim();
      // Remove phone prefix if present
      const cleanedBeforePhone = beforePhone.replace(/(?:Tel|โทร|เบอร์|เบอร์โทร|phone|mobile|มือถือ)[\s:：]*$/i, '').trim();
      // Split by newlines and take the last non-empty line before phone
      const linesBeforePhone = cleanedBeforePhone.split(/\n/).map(l => l.trim()).filter(l => l);
      if (linesBeforePhone.length > 0) {
        let lastLine = linesBeforePhone[linesBeforePhone.length - 1];
        // Strip quotes from the line if present
        lastLine = lastLine.replace(/^["""]+|["""]+$/g, '').trim();
        // Check if it looks like a name - allow parentheses and other characters, including quotes
        if (lastLine && 
            /^[ก-๙a-zA-Z\s\.\(\)\[\]\/\-"]+$/.test(lastLine) && 
            lastLine.length >= 2 && 
            lastLine.length < 80 &&
            !lastLine.match(/(ซอย|ถนน|แขวง|เขต|จังหวัด|อำเภอ|ตำบล|ม\.|หมู่|บริษัท)/)) {
          // Remove any remaining quotes from the name
          result.name = lastLine.replace(/["""]+/g, '').trim();
        }
      }
    }
  }

  return result;
};

/**
 * Get all locations for a postcode
 * @param {string} postcode 
 * @returns {Array} All locations with this postcode
 */
export const getLocationsByPostcode = (postcode) => {
  if (!postcode || !isDatabaseAvailable()) {
    return [];
  }
  
  return thaiAddressDatabase.filter(record => record.postcode === postcode);
};

/**
 * Find location matches in the text with scoring
 * Returns all possible matches for user to choose from
 * 
 * @param {string} text - Text to search in
 * @param {string} postcode - Extracted postcode (optional)
 * @returns {Object} { candidates, exactMatch, hasPostcode }
 */
export const findLocationCandidates = (text, postcode) => {
  if (!isDatabaseAvailable()) {
    return { candidates: [], exactMatch: null, hasPostcode: false };
  }

  const normalizedText = normalizeThaiText(text).toLowerCase();
  
  // Extract subdistrict, district, and province from explicit markers
  let extractedSubdistrict = null;
  let extractedDistrict = null;
  let extractedProvince = null;
  
  // Match patterns like: ต.บางขุนกอง, ตำบลบางขุนกอง, แขวงบางขุนกอง
  // Try multiple patterns to catch variations
  const subdistrictPatterns = [
    /(?:ต\.|ตำบล|แขวง)\s+([ก-๙]+)/,  // ต.บางขุนกอง (with space)
    /(?:ต\.|ตำบล|แขวง)([ก-๙]+)/,     // ต.บางขุนกอง (no space)
    /(?:ตำบล|แขวง)\s*([ก-๙]+)/       // ตำบลบางขุนกอง
  ];
  
  for (const pattern of subdistrictPatterns) {
    const match = normalizedText.match(pattern);
    if (match && match[1]) {
      extractedSubdistrict = match[1].trim().replace(/\s+/g, '');
      break;
    }
  }
  
  // Match patterns like: อ.บางกรวย, อำเภอบางกรวย, เขตบางกรวย
  const districtPatterns = [
    /(?:อ\.|อำเภอ|เขต)\s+([ก-๙]+)/,  // อ.บางกรวย (with space)
    /(?:อ\.|อำเภอ|เขต)([ก-๙]+)/,     // อ.บางกรวย (no space)
    /(?:อำเภอ|เขต)\s*([ก-๙]+)/       // อำเภอบางกรวย
  ];
  
  for (const pattern of districtPatterns) {
    const match = normalizedText.match(pattern);
    if (match && match[1]) {
      extractedDistrict = match[1].trim().replace(/\s+/g, '');
      break;
    }
  }
  
  // Match patterns like: จ.สุพรรณบุรี, จังหวัดสุพรรณบุรี
  const provincePatterns = [
    /(?:จ\.|จังหวัด)\s+([ก-๙]+)/,     // จ.สุพรรณบุรี (with space)
    /(?:จ\.|จังหวัด)([ก-๙]+)/,        // จ.สุพรรณบุรี (no space)
    /(?:จังหวัด)\s*([ก-๙]+)/          // จังหวัดสุพรรณบุรี
  ];
  
  for (const pattern of provincePatterns) {
    const match = normalizedText.match(pattern);
    if (match && match[1]) {
      extractedProvince = match[1].trim().replace(/\s+/g, '');
      break;
    }
  }
  
  // If province not extracted from marker, try to find it in text after district
  if (!extractedProvince && extractedDistrict) {
    // Look for province name that appears after district in the text
    // Common pattern: "อ.เมือง จ.สุพรรณบุรี" or "อำเภอเมือง สุพรรณบุรี"
    const provinceAfterDistrict = normalizedText.match(new RegExp(
      `(?:${extractedDistrict.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|อ\\.เมือง|อำเภอเมือง|เขตเมือง)\\s+(?:จ\\.|จังหวัด)?\\s*([ก-๙]+)`
    ));
    if (provinceAfterDistrict && provinceAfterDistrict[1]) {
      extractedProvince = provinceAfterDistrict[1].trim().replace(/\s+/g, '');
    }
  }
  
  // Special case: If district is just "เมือง" and we have province, expand to "เมือง[province]"
  // Example: "อ.เมือง" + "จ.สุพรรณบุรี" -> "เมืองสุพรรณบุรี"
  if (extractedDistrict && extractedDistrict === 'เมือง' && extractedProvince) {
    extractedDistrict = `เมือง${extractedProvince}`;
  }
  
  // Normalize extracted values for comparison
  const normalizedExtractedSubdistrict = extractedSubdistrict ? normalizeThaiText(extractedSubdistrict).replace(/\s+/g, '').toLowerCase() : null;
  const normalizedExtractedDistrict = extractedDistrict ? normalizeThaiText(extractedDistrict).replace(/\s+/g, '').toLowerCase() : null;
  const normalizedExtractedProvince = extractedProvince ? normalizeThaiText(extractedProvince).replace(/\s+/g, '').toLowerCase() : null;
  
  // If we have postcode, get all locations for it
  if (postcode) {
    const postcodeLocations = getLocationsByPostcode(postcode);
    console.log('[ThaiAddress] findLocationCandidates by postcode', { postcode, locationCount: postcodeLocations.length, sample: postcodeLocations.slice(0, 3).map(l => ({ subdistrict: l.subdistrict, district: l.district, province: l.province })) });

    if (postcodeLocations.length === 0) {
      return { candidates: [], exactMatch: null, hasPostcode: true, invalidPostcode: true };
    }
    
    // HIERARCHICAL CHECKING: Check from smallest to largest (subdistrict -> district -> province)
    // Score each based on text matching with hierarchy
    const scored = postcodeLocations.map(record => {
      let score = 0;
      const matchedParts = [];
      
      const recordSubdistrict = record.subdistrict ? record.subdistrict.toLowerCase() : '';
      const recordDistrict = record.district ? record.district.toLowerCase() : '';
      const recordProvince = record.province ? record.province.toLowerCase() : '';

      // Normalize record values (remove spaces, normalize Unicode)
      const normalizedRecordSubdistrict = normalizeThaiText(recordSubdistrict).replace(/\s+/g, '').toLowerCase();
      const normalizedRecordDistrict = normalizeThaiText(recordDistrict).replace(/\s+/g, '').toLowerCase();
      
      // STRICT MODE: If we extracted markers, they must match correctly
      let hasMarkerMismatch = false;
      
      // STEP 1: Check subdistrict first (most specific - ตำบล/แขวง)
      if (normalizedExtractedSubdistrict) {
        // We have extracted subdistrict from marker (ต., ตำบล, แขวง)
        if (normalizedRecordSubdistrict === normalizedExtractedSubdistrict) {
          // Perfect match - highest priority (100 points)
          score += 100;
          matchedParts.push('subdistrict');
        } else {
          // Mismatch - this record is definitely wrong
          hasMarkerMismatch = true;
          score -= 1000; // Very heavy penalty - reject this record
        }
      } else {
        // No marker extracted, check if subdistrict appears in text
        if (recordSubdistrict && normalizedText.includes(recordSubdistrict)) {
          score += 30; // Lower score without marker
          matchedParts.push('subdistrict');
        }
      }
      
      // STEP 2: Check district second (less specific - อำเภอ/เขต)
      if (normalizedExtractedDistrict) {
        // We have extracted district from marker (อ., อำเภอ, เขต)
        if (normalizedRecordDistrict === normalizedExtractedDistrict) {
          // Perfect match - high priority (50 points)
          score += 50;
          matchedParts.push('district');
        } else {
          // Mismatch - this record is wrong
          hasMarkerMismatch = true;
          score -= 1000; // Very heavy penalty - reject this record
        }
      } else {
        // No marker extracted, check if district appears in text
        if (recordDistrict && normalizedText.includes(recordDistrict)) {
          score += 20; // Lower score without marker
          matchedParts.push('district');
        }
      }
      
      // STEP 3: Check province last (least specific - จังหวัด)
      const normalizedRecordProvince = normalizeThaiText(recordProvince).replace(/\s+/g, '').toLowerCase();
      
      if (normalizedExtractedProvince) {
        // We have extracted province from marker (จ., จังหวัด)
        if (normalizedRecordProvince === normalizedExtractedProvince) {
          // Perfect match - medium priority (15 points)
          score += 15;
          matchedParts.push('province');
        } else {
          // Mismatch - but don't reject completely (province is less critical)
          score -= 5; // Small penalty
        }
      } else {
        // No marker extracted, check if province appears in text
        if (recordProvince && normalizedText.includes(recordProvince)) {
          score += 10; // Lowest priority
          matchedParts.push('province');
        }
      }
      
      // Perfect match bonus: if both extracted markers match AND no mismatches
      if (normalizedExtractedSubdistrict && normalizedExtractedDistrict && 
          normalizedRecordSubdistrict === normalizedExtractedSubdistrict && 
          normalizedRecordDistrict === normalizedExtractedDistrict &&
          !hasMarkerMismatch) {
        score += 200; // Huge bonus for perfect match (total: 350 points)
      }

      return { ...record, score, matchedParts, hasMarkerMismatch };
    })
    // Filter out records with marker mismatches
    .filter(record => {
      // If we have extracted markers, completely reject mismatches
      if (normalizedExtractedSubdistrict || normalizedExtractedDistrict) {
        return !record.hasMarkerMismatch;
      }
      return true;
    })
    .sort((a, b) => {
      // Sort by: no mismatch first, then by score (highest first)
      if (a.hasMarkerMismatch !== b.hasMarkerMismatch) {
        return a.hasMarkerMismatch ? 1 : -1;
      }
      return b.score - a.score;
    });

    // Check if we have an exact match (all 3 parts found in text)
    const exactMatch = scored.find(s => s.matchedParts.length === 3) || null;
    const candidateCount = exactMatch ? 1 : scored.length;
    console.log('[ThaiAddress] findLocationCandidates result', { postcode, exactMatch: !!exactMatch, candidateCount, candidates: (exactMatch ? [exactMatch] : scored).slice(0, 5).map(c => ({ subdistrict: c.subdistrict, district: c.district, province: c.province, postcode: c.postcode })) });

    // If exact match, only return that one
    if (exactMatch) {
      return {
        candidates: [exactMatch],
        exactMatch,
        hasPostcode: true
      };
    }

    // Return all candidates for user to choose
    return {
      candidates: scored,
      exactMatch: null,
      hasPostcode: true
    };
  }

  // No postcode - try text search (less reliable)
  const matches = new Map();
  
  for (const record of thaiAddressDatabase) {
    let score = 0;
    const matchedParts = [];

    if (record.subdistrict && normalizedText.includes(record.subdistrict.toLowerCase())) {
      score += 3;
      matchedParts.push('subdistrict');
    }
    if (record.district && normalizedText.includes(record.district.toLowerCase())) {
      score += 2;
      matchedParts.push('district');
    }
    if (record.province && normalizedText.includes(record.province.toLowerCase())) {
      score += 1;
      matchedParts.push('province');
    }

    // Need at least 2 parts for non-postcode search
    if (matchedParts.length >= 2) {
      const key = `${record.subdistrict}-${record.district}-${record.province}`;
      if (!matches.has(key) || matches.get(key).score < score) {
        matches.set(key, { ...record, score, matchedParts });
      }
    }
  }

  const candidates = Array.from(matches.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  const exactMatch = candidates.find(c => c.matchedParts.length >= 3) || null;

  return { 
    candidates, 
    exactMatch, 
    hasPostcode: false 
  };
};

/**
 * Build clean address by removing name, phone, location parts from text
 * @param {string} text - Original text
 * @param {Object} basicInfo - Extracted basic info
 * @param {Object} location - Matched location (optional)
 * @returns {string} Clean address
 */
export const buildCleanAddress = (text, basicInfo, location) => {
  if (!text) return '';
  
  // Strip outer quotes first
  let processedText = text.trim();
  if ((processedText.startsWith('"') && processedText.endsWith('"')) || 
      (processedText.startsWith('"') && processedText.endsWith('"'))) {
    processedText = processedText.slice(1, -1).trim();
  }
  
  let cleanText = normalizeThaiText(processedText);
  
  // Remove name if found (handle newlines and exact matches, also handle quotes around name)
  if (basicInfo?.name) {
    // Remove name exactly as it appears (with or without newlines)
    const namePattern = basicInfo.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    cleanText = cleanText.replace(new RegExp(namePattern, 'g'), ' ');
    // Also try with quotes around the name
    cleanText = cleanText.replace(new RegExp(`[""]?${namePattern}[""]?`, 'g'), ' ');
    // Also remove if it's on its own line (with or without quotes)
    cleanText = cleanText.replace(new RegExp(`^\\s*[""]?${namePattern}[""]?\\s*$`, 'gm'), ' ');
  }
  
  // Remove phone and phone prefixes
  if (basicInfo?.phone) {
    // Remove phone number with various formats
    cleanText = cleanText.replace(new RegExp(basicInfo.phone.replace(/(\d)/g, '$1[-\\s]?'), 'g'), ' ');
    cleanText = cleanText.replace(basicInfo.phone, ' ');
    
    // Remove phone prefixes (Tel :, Tel:, โทร, etc.)
    if (basicInfo?.phonePrefix) {
      cleanText = cleanText.replace(new RegExp(basicInfo.phonePrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' ');
    }
    // Also try common phone prefix patterns
    cleanText = cleanText.replace(/(?:Tel|โทร|เบอร์|เบอร์โทร|phone|mobile|มือถือ)[\s:：]*/gi, ' ');
  }
  
  // Remove location parts if provided
  if (location) {
    if (location.subdistrict) {
      cleanText = cleanText.replace(new RegExp(`(ตำบล|ต\\.|แขวง)?\\s*${location.subdistrict}`, 'gi'), ' ');
    }
    if (location.district) {
      cleanText = cleanText.replace(new RegExp(`(อำเภอ|อ\\.|เขต)?\\s*${location.district}`, 'gi'), ' ');
    }
    if (location.province) {
      cleanText = cleanText.replace(new RegExp(`(จังหวัด|จ\\.)?\\s*${location.province}`, 'gi'), ' ');
    }
    if (location.postcode) {
      cleanText = cleanText.replace(new RegExp(`\\b${location.postcode}\\b`, 'g'), ' ');
    }
  } else if (basicInfo?.postcode) {
    cleanText = cleanText.replace(new RegExp(`\\b${basicInfo.postcode}\\b`, 'g'), ' ');
  }
  
  // Clean up multiple spaces and trim
  return cleanText.replace(/\s+/g, ' ').trim();
};

/**
 * Smart parse - extracts basic info and finds location candidates
 * @param {string} fullText - Raw pasted text
 * @returns {Object} { phone, postcode, name, candidates, exactMatch, hasPostcode, needsSelection }
 */
export const smartParseAddress = (fullText) => {
  const basicInfo = extractBasicInfo(fullText);
  
  // Find location candidates
  const locationResult = findLocationCandidates(
    basicInfo.cleanText || fullText, 
    basicInfo.postcode
  );

  // Determine if user needs to select
  const needsSelection = !locationResult.exactMatch && locationResult.candidates.length > 1;
  
  // Build preview address (will be rebuilt when location is selected)
  const previewAddress = buildCleanAddress(fullText, basicInfo, locationResult.exactMatch);

  return {
    ...basicInfo,
    ...locationResult,
    needsSelection,
    address: previewAddress
  };
};
